// src/admin/components/apartments/ApartmentList.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiAddLine,
  RiFilter2Line,
  RiSearchLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiCloseCircleLine,
  RiCheckLine,
  RiAlertLine,
  RiRefreshLine,
  RiMapPinLine,
  RiRulerLine,
  RiLayoutGridLine,
  RiListUnordered,
  RiCloseLine,
  RiCalendar2Line,
  RiInformationLine,
  RiShieldCheckLine,
  RiBuilding3Line,
  RiLayoutMasonryLine,
  RiBox3Line,
  RiLightbulbLine,
} from 'react-icons/ri';
import {
  getAllApartments,
  deleteApartment,
  updateApartmentStatus,
  getAllBuildings,
  getAllApartmentTypes,
  getApartmentStatusColor,
  APARTMENT_STATUS_OPTIONS,
} from '../../../models/apartmentModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser } from '../../../models/authModel';

const ApartmentList = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [apartments,     setApartments]     = useState([]);
  const [buildings,      setBuildings]      = useState([]);
  const [apartmentTypes, setApartmentTypes] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [filterOpen,     setFilterOpen]     = useState(false);
  const [viewMode,       setViewMode]       = useState(() => localStorage.getItem('showroomList_mode') || 'grid');

  const [filters, setFilters] = useState({
    buildingId: '',
    type:       '',
    status:     '',
    searchTerm: '',
    isFurnished: '', // Utilities Active
  });

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, apartmentId: null, apartmentNumber: '' });
  const [deleting,        setDeleting]        = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [buildingsResponse, typesResponse] = await Promise.all([getAllBuildings(), getAllApartmentTypes()]);
        setBuildings(buildingsResponse.buildings || []);
        const typesList = typesResponse.types?.map(type => ({ id: type.type, name: type.type })) || [];
        setApartmentTypes(typesList);
      } catch (err) {
        console.error('Error fetching initial data:', err);
      }
    };
    fetchInitialData();
  }, []);

  const fetchApartments = async () => {
    try {
      setLoading(true);
      const apiFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value && key !== 'searchTerm') {
          if (key === 'isFurnished') acc[key] = value === 'true';
          else acc[key] = value;
        }
        return acc;
      }, {});
      const response = await getAllApartments(apiFilters);
      setApartments(response?.apartments || []);
    } catch (err) {
      console.error('Error fetching showrooms:', err);
      const errorMessages = {
        401: t('auth_failed_login_again', 'Authentication failed. Please log in again.'),
        403: t('access_denied_admin_role', 'Access denied. Admin role required.'),
        500: t('server_error_backend_connection', 'Server error. Please check backend connection.'),
      };
      setError(errorMessages[err.response?.status] ||
        (err.message.includes('Network Error') || err.code === 'ERR_NETWORK'
          ? t('cannot_connect_to_server', 'Cannot connect to server. Please check your network.')
          : t('failed_to_load_showroom_data', 'Failed to load showroom data')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApartments();
    setIsFilterApplied(filters.buildingId !== '' || filters.type !== '' || filters.status !== '' || filters.isFurnished !== '');
  }, [filters.buildingId, filters.type, filters.status, filters.isFurnished]);

  useEffect(() => {
    localStorage.setItem('showroomList_mode', viewMode);
  }, [viewMode]);

  const filteredApartments = apartments.filter(apartment => {
    const searchTerm = filters.searchTerm.toLowerCase();
    return searchTerm === '' ||
      apartment.apartmentNumber?.toString().toLowerCase().includes(searchTerm) ||
      apartment.buildingName?.toLowerCase().includes(searchTerm) ||
      apartment.type?.toLowerCase().includes(searchTerm);
  });

  const handleSearch    = (e) => setFilters({ ...filters, searchTerm: e.target.value });
  const toggleViewMode  = () => setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  const resetFilters    = () => setFilters({ buildingId: '', type: '', status: '', searchTerm: '', isFurnished: '' });
  const openDeleteDialog  = (apartmentId, apartmentNumber) => setDeleteDialog({ isOpen: true, apartmentId, apartmentNumber });
  const closeDeleteDialog = () => setDeleteDialog({ isOpen: false, apartmentId: null, apartmentNumber: '' });

  const handleDeleteApartment = async () => {
    if (!deleteDialog.apartmentId) return;
    try {
      setDeleting(true);
      await deleteApartment(deleteDialog.apartmentId);
      setApartments(apartments.filter(a => a.id !== deleteDialog.apartmentId));
      closeDeleteDialog();
    } catch (err) {
      console.error('Error deleting showroom:', err);
      setError(err.message || 'Failed to delete showroom');
    } finally { setDeleting(false); }
  };

  const toggleApartmentStatus = async (apartmentId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'vacant' ? 'maintenance' : 'vacant';
      await updateApartmentStatus(apartmentId, newStatus);
      setApartments(apartments.map(a => a.id === apartmentId ? { ...a, status: newStatus } : a));
    } catch (err) {
      console.error('Error toggling showroom status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  const apartmentStats = {
    vacant:      apartments.filter(p => (!p.tenants || p.tenants.length === 0) && p.status === 'vacant').length,
    occupied:    apartments.filter(p => (p.tenants && p.tenants.length > 0) || p.status === 'occupied').length,
    maintenance: apartments.filter(p => p.status === 'maintenance').length,
    total:       apartments.length,
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case 'vacant':      return { icon: <RiShieldCheckLine />,   color: isDarkMode ? 'bg-blue-900/20 text-blue-400 border-blue-900/50'    : 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'occupied':    return { icon: <RiBox3Line />,           color: isDarkMode ? 'bg-emerald-900/20 text-emerald-400 border-emerald-900/50' : 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'maintenance': return { icon: <RiCloseCircleLine />,    color: isDarkMode ? 'bg-amber-900/20 text-amber-400 border-amber-900/50' : 'bg-amber-50 text-amber-700 border-amber-200' };
      default:            return { icon: <RiInformationLine />,    color: isDarkMode ? 'bg-zinc-800 text-zinc-400 border-zinc-700'          : 'bg-zinc-100 text-zinc-600 border-zinc-300' };
    }
  };

  /* ── Delete Dialog ───────────────────────────────────────────────── */
  const renderDeleteDialog = () => (
    deleteDialog.isOpen && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className={`max-w-md w-full rounded-xl border shadow-2xl overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'}`}>
          <div className="bg-red-600 p-4 text-white flex items-center">
            <RiAlertLine size={24} className="me-3"/>
            <h3 className="text-lg font-bold uppercase tracking-wider">{t('confirm_deletion_title', 'Confirm Deletion')}</h3>
          </div>
          <div className="p-6">
            <p className={`mb-6 font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
              {t('confirm_deletion_desc', 'Are you sure you want to permanently delete showroom "{{device}}"? This action cannot be undone.', { device: deleteDialog.apartmentNumber })}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={closeDeleteDialog} disabled={deleting} className={`px-5 py-2.5 rounded-lg font-semibold transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                {t('cancel', 'Cancel')}
              </button>
              <button onClick={handleDeleteApartment} disabled={deleting} className={`px-5 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center font-semibold shadow-md transition-all ${deleting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {deleting
                  ? <><div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin me-2"/> {t('deleting', 'Deleting…')}</>
                  : <><RiDeleteBin6Line className="me-2"/> {t('delete', 'Delete')}</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  /* ── Header ──────────────────────────────────────────────────────── */
  const renderHeader = () => (
    <div dir={isRTL?'rtl':'ltr'} className={`relative overflow-hidden border-b ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
      <div className="h-1 w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600"/>
      <div className="px-6 py-5 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              <RiBox3Line className={isDarkMode ? 'text-red-500' : 'text-red-600'} size={26}/>
              {t('showroom_registry', 'Showroom Registry')}
            </h2>
            <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
              {apartments.length > 0
                ? t('manage_showroom_units', 'Manage and track {{count}} showroom units across all complexes.', { count: apartments.length })
                : t('add_first_showroom_to_start', 'Add your first showroom to get started.')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Search */}
            <div className="relative flex-grow md:flex-grow-0 md:w-64">
              <RiSearchLine className={`absolute top-1/2 -translate-y-1/2 start-3 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} size={16}/>
              <input type="search"
                className={`block w-full py-2.5 ps-9 pe-3 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode ? 'bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500' : 'bg-gray-50 border-gray-200 text-zinc-900 placeholder-zinc-400'}`}
                placeholder={t('search_apartments', "Search apartments, owners, t…")}
                value={filters.searchTerm} onChange={handleSearch}
              />
            </div>

            {/* Filter toggle */}
            <button onClick={() => setFilterOpen(!filterOpen)}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-1.5 border transition-all ${
                isFilterApplied
                  ? isDarkMode ? 'bg-red-900/20 text-red-400 border-red-900' : 'bg-red-50 text-red-700 border-red-200'
                  : isDarkMode ? 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800' : 'bg-white text-zinc-700 border-gray-200 hover:bg-gray-50'
              }`}>
              <RiFilter2Line size={16}/> {t('filter', 'Filter')}
              {isFilterApplied && (
                <span className="inline-flex items-center justify-center w-4 h-4 text-[9px] rounded-full bg-red-600 text-white">
                  {Object.values(filters).filter(v => v && v !== filters.searchTerm).length}
                </span>
              )}
            </button>

            {/* View toggle: List */}
            <button onClick={() => setViewMode('list')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-1.5 border transition-colors ${
                viewMode === 'list'
                  ? 'bg-zinc-100 text-zinc-900 border-gray-200 font-semibold'
                  : isDarkMode ? 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:bg-zinc-800' : 'bg-white text-zinc-500 border-gray-200 hover:bg-gray-50'
              }`}>
              <RiListUnordered size={16}/> {t('list_view', 'List')}
            </button>

            {/* Add */}
            <Link to="/admin/apartments/add"
              className="px-4 py-2.5 text-sm font-bold rounded-lg flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 transition-all shadow-sm">
              <RiAddLine size={16}/> {t('add_apartment', 'Add Apartment')}
            </Link>
          </div>
        </div>
      </div>

      {/* Filter panel */}
      {filterOpen && (
        <div className={`px-6 py-5 md:px-8 border-t ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{t('filter', 'Filter')}</h3>
            <div className="flex gap-2">
              {isFilterApplied && (
                <button onClick={resetFilters} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'}`}>
                  {t('reset', 'Reset')}
                </button>
              )}
              <button onClick={() => setFilterOpen(false)} className={`p-1 rounded-md ${isDarkMode ? 'text-zinc-500 hover:bg-zinc-800' : 'text-zinc-400 hover:bg-zinc-200'}`}>
                <RiCloseLine size={18}/>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('complex_park', 'Complex / Park')}</label>
              <select value={filters.buildingId} onChange={e => setFilters({...filters, buildingId: e.target.value})}
                className={`w-full p-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-200 text-zinc-900'}`}>
                <option value="">{t('all_locations', 'All Locations')}</option>
                {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('class_type', 'Class Type')}</label>
              <select value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})}
                className={`w-full p-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-200 text-zinc-900'}`}>
                <option value="">{t('all_classes', 'All Classes')}</option>
                {apartmentTypes.map(tp => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('status', 'Status')}</label>
              <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}
                className={`w-full p-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-200 text-zinc-900'}`}>
                <option value="">{t('all_statuses', 'All Statuses')}</option>
                {APARTMENT_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>{t('utilities_active', 'Utilities Active')}</label>
              <select value={filters.isFurnished} onChange={e => setFilters({...filters, isFurnished: e.target.value})}
                className={`w-full p-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-200 text-zinc-900'}`}>
                <option value="">{t('all_systems', 'All Systems')}</option>
                <option value="true">{t('yes_powered', 'Yes (Powered/Climate)')}</option>
                <option value="false">{t('none', 'None')}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ── Stats KPI Bar ───────────────────────────────────────────────── */
  const renderStats = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {[
        { label: t('vacant', 'Vacant'),    value: apartmentStats.vacant,     icon: <RiShieldCheckLine size={22}/>, iconCls: isDarkMode ? 'text-blue-400' : 'text-blue-500',    bar: 'bg-blue-500' },
        { label: t('occupied', 'Occupied'),  value: apartmentStats.occupied,   icon: <RiBox3Line size={22}/>,        iconCls: isDarkMode ? 'text-emerald-400' : 'text-emerald-500', bar: 'bg-emerald-500' },
        { label: t('maintenance', 'Maintenance'), value: apartmentStats.maintenance, icon: <RiCloseCircleLine size={22}/>, iconCls: isDarkMode ? 'text-amber-400' : 'text-amber-500',   bar: 'bg-amber-500' },
        { label: t('total', 'Total'),     value: apartmentStats.total,      icon: <RiBuilding3Line size={22}/>,   iconCls: isDarkMode ? 'text-violet-400' : 'text-violet-500',  bar: 'bg-violet-500' },
      ].map((stat, i) => (
        <div key={i} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{stat.label}</p>
              <h4 className={`text-3xl font-bold mt-0.5 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{stat.value}</h4>
            </div>
            <div className={`mt-1 ${stat.iconCls}`}>{stat.icon}</div>
          </div>
          <div className={`w-full h-1 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
            <div className={`h-full ${stat.bar}`} style={{ width: `${apartmentStats.total > 0 ? (stat.value / apartmentStats.total) * 100 : 0}%` }}/>
          </div>
        </div>
      ))}
    </div>
  );

  /* ── States ──────────────────────────────────────────────────────── */
  const renderLoading = () => loading && (
    <div className="flex justify-center py-20">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-red-500 border-t-transparent"/>
    </div>
  );

  const renderError = () => error && !loading && (
    <div className={`p-8 rounded-xl border flex flex-col items-center justify-center text-center ${isDarkMode ? 'bg-red-900/20 border-red-900/50 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
      <RiAlertLine className="text-5xl mb-4 opacity-80"/>
      <h3 className="text-xl font-bold mb-2 uppercase tracking-wide">{t('registry_error', 'Registry Error')}</h3>
      <p className="mb-6 font-medium">{error}</p>
      <button onClick={fetchApartments} className="px-6 py-2.5 font-bold uppercase tracking-wider rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center shadow-md">
        <RiRefreshLine className="me-2" size={18}/> {t('retry_connection', 'Retry Connection')}
      </button>
    </div>
  );

  const renderEmptyState = () => !loading && !error && apartments.length === 0 && (
    <div className="flex flex-col items-center justify-center py-20">
      <RiBox3Line className={`text-7xl mb-6 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`}/>
      <h3 className={`text-2xl font-black uppercase tracking-tight mb-3 ${isDarkMode ? 'text-white' : 'text-zinc-800'}`}>{t('empty_registry', 'Empty Registry')}</h3>
      <p className={`mb-8 font-medium ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('no_showrooms_logged', 'There are no showrooms logged in the system yet.')}</p>
      <Link to="/admin/apartments/add" className="px-6 py-3.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider flex items-center shadow-lg shadow-red-500/20">
        <RiAddLine className="me-2" size={20}/> {t('initialize_first_unit', 'Initialize First Unit')}
      </Link>
    </div>
  );

  const renderNoResults = () => !loading && !error && apartments.length > 0 && filteredApartments.length === 0 && (
    <div className="flex flex-col items-center justify-center py-20">
      <RiSearchLine className={`text-6xl mb-5 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`}/>
      <h3 className={`text-xl font-bold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white' : 'text-zinc-800'}`}>{t('no_matches_found', 'No Matches Found')}</h3>
      <p className={`mb-6 font-medium ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{t('adjust_parameters_to_locate', 'Adjust parameters to locate inventory slots.')}</p>
      <button onClick={resetFilters} className="px-6 py-2.5 rounded-lg border-2 font-bold uppercase tracking-wider border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors">
        {t('clear_parameters', 'Clear Parameters')}
      </button>
    </div>
  );

  /* ── Card (Grid) ─────────────────────────────────────────────────── */
  const ApartmentCard = ({ apartment }) => {
    const statusDetails = getStatusDetails(apartment.status);
    // Status dot color
    const statusDot = apartment.status === 'vacant' ? 'bg-blue-500'
      : apartment.status === 'occupied' ? 'bg-emerald-500'
      : apartment.status === 'maintenance' ? 'bg-amber-500'
      : 'bg-zinc-400';
    const statusLabel = apartment.status === 'vacant' ? t('vacant', 'Vacant') : apartment.status === 'occupied' ? t('active', 'Active') : t('maintenance', 'Maintenance');

    return (
      <div className={`group flex flex-col rounded-xl overflow-hidden border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-600' : 'bg-white border-gray-200 hover:border-gray-300'
      }`}>

        {/* Top row: type chip + status chip — NO colored background */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${
          isDarkMode ? 'border-zinc-800' : 'border-gray-100'
        }`}>
          <span className={`text-xs font-semibold ${
            isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
          }`}>{apartment.type ? t(`type_${apartment.type.toLowerCase().replace(/\\s+/g, '_')}`, apartment.type) : '—'}</span>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${statusDot}`}/>
            <span className={`text-xs font-semibold ${
              apartment.status === 'vacant'    ? isDarkMode ? 'text-blue-400'    : 'text-blue-600'
              : apartment.status === 'occupied' ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              : isDarkMode ? 'text-amber-400' : 'text-amber-600'
            }`}>{statusLabel}</span>
          </div>
        </div>

        <div className="p-4 flex flex-col flex-1">
          {/* Unit # */}
          <h3 className={`text-3xl font-bold tracking-tight mb-0.5 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
            {apartment.apartmentNumber}
          </h3>
          <div className={`flex items-center gap-1 text-xs mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
            <RiMapPinLine size={12}/>
            <span className="truncate">{apartment.buildingName || t('unknown_complex', 'Unknown Complex')}</span>
          </div>

          {/* Floor + Size with colored icons */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-zinc-800/60 border-zinc-700' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex items-center gap-1 mb-1">
                <RiBuilding3Line size={12} className={isDarkMode ? 'text-red-400' : 'text-red-500'}/>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('floor', 'Floor')}</p>
              </div>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{apartment.floor || t('n_a', 'N/A')}</p>
            </div>
            <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-zinc-800/60 border-zinc-700' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex items-center gap-1 mb-1">
                <RiRulerLine size={12} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}/>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('size', 'Size')}</p>
              </div>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{apartment.size ? `${apartment.size} m²` : '—'}</p>
            </div>
          </div>

          {apartment.isFurnished && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
              isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-300' : 'bg-gray-50 border-gray-100 text-zinc-600'
            }`}>
              <RiLightbulbLine className={isDarkMode ? 'text-yellow-400' : 'text-yellow-500'} size={14}/>
              {t('utilities_active', 'Utilities Active')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 py-3 border-t flex items-center justify-between ${
          isDarkMode ? 'border-zinc-800' : 'border-gray-100'
        }`}>
          <div className="flex gap-1.5">
            <button onClick={() => toggleApartmentStatus(apartment.id, apartment.status)}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode ? 'text-zinc-500 hover:text-white hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-700 hover:bg-gray-100'
              }`} title={t('toggle_status', 'Toggle Status')}>
              {apartment.status === 'vacant' ? <RiCloseCircleLine size={18}/> : <RiCheckLine size={18}/>}
            </button>
            <Link to={`/admin/apartments/${apartment.id}`}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode ? 'text-zinc-500 hover:text-white hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-700 hover:bg-gray-100'
              }`} title={t('view_details', 'View Details')}>
              <RiEyeLine size={18}/>
            </Link>
          </div>
          <Link to={`/admin/apartments/${apartment.id}/edit`}
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
              isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'
            }`}>
            <RiPencilLine size={14}/> {t('manage', 'Manage')}
          </Link>
        </div>
      </div>
    );
  };

  /* ── Row (List) ──────────────────────────────────────────────────── */
  const ApartmentRow = ({ apartment }) => {
    const statusDot = apartment.status === 'vacant' ? 'bg-blue-500'
      : apartment.status === 'occupied' ? 'bg-emerald-500'
      : 'bg-amber-500';
    const statusLabel = apartment.status === 'vacant' ? t('vacant', 'Vacant') : apartment.status === 'occupied' ? t('active', 'Active') : t('maintenance', 'Maintenance');
    return (
      <div className={`flex flex-col md:flex-row items-stretch rounded-xl border transition-all hover:shadow-md ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-gray-200 hover:border-gray-300'
      }`}>
        <div className="flex-1 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Status dot */}
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusDot}`}/>
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{apartment.apartmentNumber}</h3>
                {apartment.type && (
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-md border ${
                    isDarkMode ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-gray-100 text-zinc-500 border-gray-200'
                  }`}>{t(`type_${apartment.type.toLowerCase().replace(/\\s+/g, '_')}`, apartment.type)}</span>
                )}
                <span className={`text-xs font-semibold ${
                  apartment.status === 'vacant'    ? isDarkMode ? 'text-blue-400'    : 'text-blue-600'
                  : apartment.status === 'occupied' ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                  : isDarkMode ? 'text-amber-400' : 'text-amber-600'
                }`}>{statusLabel}</span>
              </div>
              <div className={`flex items-center gap-1 text-xs mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                <RiMapPinLine size={11}/>
                <span>{apartment.buildingName || t('unknown_complex', 'Unknown Complex')}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div>
              <div className="flex items-center gap-1">
                <RiBuilding3Line size={11} className={isDarkMode ? 'text-red-400' : 'text-red-500'}/>
                <p className={`text-[10px] font-semibold uppercase ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('floor', 'Floor')}</p>
              </div>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{apartment.floor || '?'}</p>
            </div>
            {apartment.size && (
              <div>
                <div className="flex items-center gap-1">
                  <RiRulerLine size={11} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}/>
                  <p className={`text-[10px] font-semibold uppercase ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('size', 'Size')}</p>
                </div>
                <p className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{apartment.size} m²</p>
              </div>
            )}
            {apartment.isFurnished && (
              <RiLightbulbLine className={isDarkMode ? 'text-yellow-400' : 'text-yellow-500'} size={18} title={t('utilities_active', 'Utilities Active')}/>
            )}
          </div>
        </div>
        <div className={`p-3 border-t md:border-t-0 md:border-s flex items-center justify-end gap-2 md:w-28 ${
          isDarkMode ? 'border-zinc-800' : 'border-gray-100'
        }`}>
          <Link to={`/admin/apartments/${apartment.id}`}
            className={`p-1.5 rounded-lg transition-colors ${
              isDarkMode ? 'text-zinc-500 hover:text-white hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-700 hover:bg-gray-100'
            }`} title={t('view', 'View')}>
            <RiEyeLine size={18}/>
          </Link>
          <Link to={`/admin/apartments/${apartment.id}/edit`}
            className={`flex items-center gap-1 text-sm font-medium transition-colors ${
              isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'
            }`}>
            <RiPencilLine size={14}/> {t('manage', 'Manage')}
          </Link>
        </div>
      </div>
    );
  };

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div dir={isRTL?'rtl':'ltr'} className="space-y-6">
      {renderDeleteDialog()}

      <div className={`rounded-xl overflow-hidden shadow-lg border ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        {renderHeader()}

        <div className="p-6 md:p-8">
          {!loading && !error && apartments.length > 0 && renderStats()}
          {renderLoading()}
          {renderError()}
          {renderEmptyState()}
          {renderNoResults()}

          {!loading && !error && filteredApartments.length > 0 && (
            <>
              <div className={`mb-4 flex justify-between items-center border-b pb-3 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}>
                <div>
                  <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-800'}`}>
                    {filteredApartments.length === 1 ? t('x_apartment_single', '1 Showroom Unit') : t('x_apartments_count', '{{count}} Showroom Units', { count: filteredApartments.length })}
                  </h3>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {t('last_updated_today', '🗓 Last updated: Today')}
                </p>
              </div>

              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredApartments.map(apt => <ApartmentCard key={apt.id} apartment={apt}/>)}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredApartments.map(apt => <ApartmentRow key={apt.id} apartment={apt}/>)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApartmentList;